import React, { createContext, useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { authApi, tokenizersApi } from '../api/smartTokenizerApi';
import { clearStoredSession, getStoredSession, storeSession, subscribeToSessionChanges } from '../api/authStorage';
import {
  mapProfileToCurrentIssuer,
  mapStatusToIssuerStatus,
  mapTokenizerToIssuer,
  toAuthorizedTokenizerRequest,
  toAuthorizedTokenizerUpdateRequest
} from '../api/smartTokenizerMappers';
import { isValidBlockchainWalletAddress } from '../blockchain/coreIcanAddress';

export const ISSUER_PERMISSIONS = {
  MINT_TOKENS: 'mint_tokens',
  BURN_TOKENS: 'burn_tokens',
  VERIFY_ASSETS: 'verify_assets',
  ASSET_NULLIFICATION: 'asset_nullification',
  OWNERSHIP_MANAGEMENT: 'ownership_management',
  ASSET_REGISTRATION: 'asset_registration',
  PHYSICAL_DELIVERY: 'physical_delivery',
  DELIVERY_VERIFICATION: 'delivery_verification',
  WAREHOUSE_OPERATIONS: 'warehouse_operations',
  REDEMPTION_PROCESSING: 'redemption_processing',
  VALUATION_UPDATES: 'valuation_updates',
  FRACTIONAL_MANAGEMENT: 'fractional_management',
  COMPLIANCE_VERIFICATION: 'compliance_verification',
  REGULATORY_REPORTING: 'regulatory_reporting',
  AUDIT_ACCESS: 'audit_access',
  USER_MANAGEMENT: 'user_management',
  ISSUER_MANAGEMENT: 'issuer_management',
  SYSTEM_CONFIGURATION: 'system_configuration'
};

export const ISSUER_STATUS = {
  PENDING_VERIFICATION: 'pending_verification',
  ACTIVE: 'active',
  SUSPENDED: 'suspended',
  REVOKED: 'revoked',
  UNDER_REVIEW: 'under_review'
};

export const ISSUER_TYPES = {
  INSTITUTIONAL: {
    id: 'institutional',
    name: 'Institutional Issuer',
    description: 'Large financial institutions, banks, custody providers',
    defaultPermissions: [
      ISSUER_PERMISSIONS.MINT_TOKENS,
      ISSUER_PERMISSIONS.BURN_TOKENS,
      ISSUER_PERMISSIONS.VERIFY_ASSETS,
      ISSUER_PERMISSIONS.REDEMPTION_PROCESSING,
      ISSUER_PERMISSIONS.COMPLIANCE_VERIFICATION,
      ISSUER_PERMISSIONS.REGULATORY_REPORTING
    ]
  },
  SPECIALIZED_CUSTODIAN: {
    id: 'specialized_custodian',
    name: 'Specialized Custodian',
    description: 'Specialized asset custodians (precious metals, art, etc.)',
    defaultPermissions: [
      ISSUER_PERMISSIONS.MINT_TOKENS,
      ISSUER_PERMISSIONS.BURN_TOKENS,
      ISSUER_PERMISSIONS.VERIFY_ASSETS,
      ISSUER_PERMISSIONS.PHYSICAL_DELIVERY,
      ISSUER_PERMISSIONS.WAREHOUSE_OPERATIONS,
      ISSUER_PERMISSIONS.ASSET_NULLIFICATION
    ]
  },
  WAREHOUSE_OPERATOR: {
    id: 'warehouse_operator',
    name: 'Warehouse Operator',
    description: 'Physical storage and logistics providers',
    defaultPermissions: [
      ISSUER_PERMISSIONS.VERIFY_ASSETS,
      ISSUER_PERMISSIONS.PHYSICAL_DELIVERY,
      ISSUER_PERMISSIONS.DELIVERY_VERIFICATION,
      ISSUER_PERMISSIONS.WAREHOUSE_OPERATIONS
    ]
  },
  ASSET_ORIGINATOR: {
    id: 'asset_originator',
    name: 'Asset Originator',
    description: 'Original asset owners and creators',
    defaultPermissions: [
      ISSUER_PERMISSIONS.ASSET_REGISTRATION,
      ISSUER_PERMISSIONS.VERIFY_ASSETS,
      ISSUER_PERMISSIONS.OWNERSHIP_MANAGEMENT,
      ISSUER_PERMISSIONS.VALUATION_UPDATES
    ]
  },
  REGULATORY_ENTITY: {
    id: 'regulatory_entity',
    name: 'Regulatory Entity',
    description: 'Regulatory bodies and compliance organizations',
    defaultPermissions: [
      ISSUER_PERMISSIONS.COMPLIANCE_VERIFICATION,
      ISSUER_PERMISSIONS.REGULATORY_REPORTING,
      ISSUER_PERMISSIONS.AUDIT_ACCESS
    ]
  }
};

export const GEOGRAPHIC_REGIONS = {
  NORTH_AMERICA: 'north_america',
  EUROPE: 'europe',
  ASIA_PACIFIC: 'asia_pacific',
  MIDDLE_EAST: 'middle_east',
  LATIN_AMERICA: 'latin_america',
  AFRICA: 'africa',
  GLOBAL: 'global'
};

const TokenIssuerContext = createContext();

const normalizeRolesFromAuth = (payload) => {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload.roles) && payload.roles.length > 0) {
    return payload.roles;
  }

  if (payload.role) {
    return [payload.role];
  }

  return [];
};

const initialState = {
  issuers: [],
  currentIssuer: null,
  userRoles: normalizeRolesFromAuth(getStoredSession()),
  session: getStoredSession(),
  permissions: ISSUER_PERMISSIONS,
  issuerTypes: ISSUER_TYPES,
  pendingRequests: [],
  issuanceHistory: [],
  complianceReports: [],
  auditLogs: [],
  loading: false,
  error: null,
  statistics: {
    totalIssuers: 0,
    activeIssuers: 0,
    totalIssuances: 0,
    totalBurned: 0,
    pendingVerifications: 0,
    pendingRedemptions: 0,
    totalVolume: 0
  }
};

const TOKEN_ISSUER_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_ISSUERS: 'SET_ISSUERS',
  ADD_ISSUER: 'ADD_ISSUER',
  UPDATE_ISSUER: 'UPDATE_ISSUER',
  SET_CURRENT_ISSUER: 'SET_CURRENT_ISSUER',
  SET_SESSION: 'SET_SESSION',
  SET_USER_ROLES: 'SET_USER_ROLES',
  PROCESS_ISSUANCE: 'PROCESS_ISSUANCE',
  PROCESS_BURNING: 'PROCESS_BURNING',
  UPDATE_STATISTICS: 'UPDATE_STATISTICS'
};

const tokenIssuerReducer = (state, action) => {
  switch (action.type) {
    case TOKEN_ISSUER_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case TOKEN_ISSUER_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    case TOKEN_ISSUER_ACTIONS.SET_ISSUERS:
      return { ...state, issuers: action.payload, loading: false, error: null };
    case TOKEN_ISSUER_ACTIONS.ADD_ISSUER:
      return { ...state, issuers: [...state.issuers, action.payload], loading: false };
    case TOKEN_ISSUER_ACTIONS.UPDATE_ISSUER:
      return {
        ...state,
        issuers: state.issuers.map((issuer) =>
          issuer.id === action.payload.id ? { ...issuer, ...action.payload } : issuer
        ),
        loading: false
      };
    case TOKEN_ISSUER_ACTIONS.SET_CURRENT_ISSUER:
      return { ...state, currentIssuer: action.payload };
    case TOKEN_ISSUER_ACTIONS.SET_SESSION:
      return { ...state, session: action.payload };
    case TOKEN_ISSUER_ACTIONS.SET_USER_ROLES:
      return { ...state, userRoles: action.payload || [] };
    case TOKEN_ISSUER_ACTIONS.PROCESS_ISSUANCE:
      return {
        ...state,
        issuanceHistory: [...state.issuanceHistory, action.payload]
      };
    case TOKEN_ISSUER_ACTIONS.PROCESS_BURNING:
      return {
        ...state,
        statistics: {
          ...state.statistics,
          totalBurned: state.statistics.totalBurned + (action.payload.amount || 0)
        }
      };
    case TOKEN_ISSUER_ACTIONS.UPDATE_STATISTICS:
      return {
        ...state,
        statistics: { ...state.statistics, ...action.payload }
      };
    default:
      return state;
  }
};

const isValidWalletAddress = (value) => isValidBlockchainWalletAddress(String(value || '').trim());

export const validateIssuerData = (issuerData) => {
  const errors = {};

  if (!issuerData.name?.trim()) errors.name = 'Organization name is required';
  if (!issuerData.registrationNumber?.trim()) {
    errors.registrationNumber = 'Registration number is required';
  }
  if (!issuerData.walletAddress?.trim()) {
    errors.walletAddress = 'Wallet address is required';
  } else if (!isValidWalletAddress(issuerData.walletAddress)) {
    errors.walletAddress =
      'Enter a valid Core ICAN address (44 hex characters: network prefix, checksum, and 20-byte account body).';
  }
  if (!issuerData.allowedCategories?.length) {
    errors.allowedCategories = 'Select at least one authorized sector (asset category)';
  }

  return { isValid: Object.keys(errors).length === 0, errors };
};

export const TokenIssuerProvider = ({ children }) => {
  const [state, dispatch] = useReducer(tokenIssuerReducer, initialState);

  const issuersRef = useRef(state.issuers);
  issuersRef.current = state.issuers;

  const hydrateCurrentIssuer = useCallback(
    async (sessionOverride = getStoredSession(), issuersOverride = issuersRef.current) => {
      if (!sessionOverride?.accessToken) {
        dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_CURRENT_ISSUER, payload: null });
        return null;
      }

      try {
        const { data: profile } = await authApi.getProfile();
        const issuersList = issuersOverride ?? issuersRef.current;
        const assignmentId =
          profile.authorizedTokenizerId ??
          profile.AuthorizedTokenizerId ??
          null;
        const matchingTokenizer =
          (assignmentId &&
            issuersList.find(
              (issuer) =>
                String(issuer.tokenizerId || issuer.id || '') === String(assignmentId)
            )) ||
          issuersList.find(
            (issuer) =>
              issuer.name?.toLowerCase() === profile.organizationName?.toLowerCase() ||
              issuer.registrationNumber === profile.organizationName
          ) ||
          null;

        const currentIssuer = mapProfileToCurrentIssuer(profile, matchingTokenizer);
        dispatch({
          type: TOKEN_ISSUER_ACTIONS.SET_USER_ROLES,
          payload: profile.roles || []
        });
        dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_CURRENT_ISSUER, payload: currentIssuer });
        return currentIssuer;
      } catch (error) {
        clearStoredSession();
        dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_CURRENT_ISSUER, payload: null });
        dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_SESSION, payload: null });
        dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_USER_ROLES, payload: [] });
        return null;
      }
    },
    []
  );

  const loadIssuers = useCallback(async () => {
    if (!getStoredSession()?.accessToken) {
      dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_ISSUERS, payload: [] });
      return [];
    }

    dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_LOADING, payload: true });

    try {
      const { data } = await tokenizersApi.list();
      const issuers = data.map(mapTokenizerToIssuer);
      dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_ISSUERS, payload: issuers });
      return issuers;
    } catch (error) {
      dispatch({
        type: TOKEN_ISSUER_ACTIONS.SET_ERROR,
        payload: error.response?.data?.message || error.message
      });
      return [];
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToSessionChanges((session) => {
      dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_SESSION, payload: session });
      dispatch({
        type: TOKEN_ISSUER_ACTIONS.SET_USER_ROLES,
        payload: normalizeRolesFromAuth(session)
      });

      if (!session) {
        dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_CURRENT_ISSUER, payload: null });
      }
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      const issuers = await loadIssuers();
      await hydrateCurrentIssuer(getStoredSession(), issuers);
    };

    bootstrap();
  }, [hydrateCurrentIssuer, loadIssuers]);

  useEffect(() => {
    const stats = {
      totalIssuers: state.issuers.length,
      activeIssuers: state.issuers.filter((issuer) => issuer.status === ISSUER_STATUS.ACTIVE).length,
      totalIssuances: state.issuanceHistory.length,
      totalVolume: state.issuanceHistory.reduce((sum, issuance) => sum + (issuance.amount || 0), 0),
      pendingVerifications: state.issuers.filter(
        (issuer) => issuer.status === ISSUER_STATUS.PENDING_VERIFICATION
      ).length,
      totalBurned: state.statistics.totalBurned
    };

    dispatch({ type: TOKEN_ISSUER_ACTIONS.UPDATE_STATISTICS, payload: stats });
  }, [state.issuers, state.issuanceHistory, state.statistics.totalBurned]);

  const loginIssuer = async ({ email, password }) => {
    dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_LOADING, payload: true });

    try {
      const { data } = await authApi.login({ email, password });
      storeSession(data);
      dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_SESSION, payload: data });
      dispatch({
        type: TOKEN_ISSUER_ACTIONS.SET_USER_ROLES,
        payload: normalizeRolesFromAuth(data)
      });

      const issuers = await loadIssuers();
      const currentIssuer = await hydrateCurrentIssuer(data, issuers);
      dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_LOADING, payload: false });

      return currentIssuer;
    } catch (error) {
      const apiMessage = error.response?.data?.message;
      const fallback =
        error.code === 'ERR_NETWORK' || error.message === 'Network Error'
          ? 'Cannot reach the API. Check REACT_APP_CORE_TOKENIZER_API_URL matches your backend (e.g. https://api-develop.tearsoftraders.com/tokenizer) and that CORS is enabled.'
          : error.message || 'Invalid email or password';
      const message = apiMessage || fallback;
      dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_ERROR, payload: message });
      dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_LOADING, payload: false });
      throw new Error(message);
    }
  };

  const refreshProfile = async () => hydrateCurrentIssuer();

  const addIssuer = async (issuerData) => {
    dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_LOADING, payload: true });

    const validation = validateIssuerData(issuerData);
    if (!validation.isValid) {
      const message = `Validation failed: ${Object.values(validation.errors).join(', ')}`;
      dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_ERROR, payload: message });
      dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_LOADING, payload: false });
      return { success: false, error: message };
    }

    try {
      const request = toAuthorizedTokenizerRequest(issuerData);
      const { data } = await tokenizersApi.create(request);
      const issuer = mapTokenizerToIssuer(data);

      dispatch({ type: TOKEN_ISSUER_ACTIONS.ADD_ISSUER, payload: issuer });
      return { success: true, issuer };
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_ERROR, payload: message });
      return { success: false, error: message };
    } finally {
      dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const updateIssuer = async (issuerId, updates) => {
    dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_LOADING, payload: true });

    try {
      const existingIssuer = state.issuers.find((issuer) => issuer.id === issuerId);
      let response;

      if (updates.status) {
        const requestedStatus = mapStatusToIssuerStatus(updates.status) || updates.status;

        if (requestedStatus === ISSUER_STATUS.ACTIVE) {
          response = await tokenizersApi.approve(issuerId);
        } else if (requestedStatus === ISSUER_STATUS.SUSPENDED || requestedStatus === ISSUER_STATUS.REVOKED) {
          response = await tokenizersApi.suspend(issuerId);
        } else {
          response = await tokenizersApi.update(
            issuerId,
            toAuthorizedTokenizerUpdateRequest(existingIssuer || {}, updates)
          );
        }
      } else {
        response = await tokenizersApi.update(
          issuerId,
          toAuthorizedTokenizerUpdateRequest(existingIssuer || {}, updates)
        );
      }

      const issuer = mapTokenizerToIssuer(response.data);
      dispatch({ type: TOKEN_ISSUER_ACTIONS.UPDATE_ISSUER, payload: issuer });

      if (state.currentIssuer?.tokenizerId === issuer.id) {
        dispatch({
          type: TOKEN_ISSUER_ACTIONS.SET_CURRENT_ISSUER,
          payload: {
            ...state.currentIssuer,
            ...issuer
          }
        });
      }

      return { success: true, issuer };
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_ERROR, payload: message });
      return { success: false, error: message };
    } finally {
      dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_LOADING, payload: false });
    }
  };

  const fetchTokenizerById = useCallback(async (id) => {
    if (!id || !getStoredSession()?.accessToken) {
      return null;
    }

    const { data } = await tokenizersApi.getById(id);
    return mapTokenizerToIssuer(data);
  }, []);

  const removeIssuer = async (issuerId) => updateIssuer(issuerId, { status: ISSUER_STATUS.SUSPENDED });

  const setCurrentIssuer = (issuer) => {
    dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_CURRENT_ISSUER, payload: issuer });
  };

  const clearCurrentIssuer = () => {
    clearStoredSession();
    dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_SESSION, payload: null });
    dispatch({ type: TOKEN_ISSUER_ACTIONS.SET_CURRENT_ISSUER, payload: null });
  };

  const hasPermission = (issuerId, permission) => {
    const issuer =
      state.issuers.find((item) => item.id === issuerId) ||
      (state.currentIssuer?.id === issuerId ? state.currentIssuer : null);

    return issuer?.permissions?.includes(permission) && issuer?.status === ISSUER_STATUS.ACTIVE;
  };

  const canOperateInCategory = (issuerId, category) => {
    const issuer =
      state.issuers.find((item) => item.id === issuerId) ||
      (state.currentIssuer?.id === issuerId ? state.currentIssuer : null);

    return issuer?.allowedCategories?.includes(category) && issuer?.status === ISSUER_STATUS.ACTIVE;
  };

  const canOperateInLocation = (issuerId, location) => {
    const issuer =
      state.issuers.find((item) => item.id === issuerId) ||
      (state.currentIssuer?.id === issuerId ? state.currentIssuer : null);

    return (
      issuer?.allowedLocations?.includes(location) ||
      issuer?.allowedRegions?.includes(GEOGRAPHIC_REGIONS.GLOBAL)
    );
  };

  const grantPermission = async () => ({
    success: false,
    error: 'Authorized tokenizer permissions are not stored via this API; they follow user roles and approval status.'
  });

  const revokePermission = async () => ({
    success: false,
    error: 'Authorized tokenizer permissions are not stored via this API; they follow user roles and approval status.'
  });

  const processTokenIssuance = async (issuerId, issuanceData) => {
    if (!hasPermission(issuerId, ISSUER_PERMISSIONS.MINT_TOKENS)) {
      return { success: false, error: 'Issuer does not have minting permissions' };
    }

    if (!canOperateInCategory(issuerId, issuanceData.category)) {
      return { success: false, error: 'Issuer not authorized for this asset category' };
    }

    const issuanceRecord = {
      id: `issuance-${Date.now()}`,
      issuerId,
      ...issuanceData,
      timestamp: new Date().toISOString(),
      status: 'completed'
    };

    dispatch({ type: TOKEN_ISSUER_ACTIONS.PROCESS_ISSUANCE, payload: issuanceRecord });
    return { success: true, issuance: issuanceRecord };
  };

  const processTokenBurning = async (issuerId, burnData) => {
    if (!hasPermission(issuerId, ISSUER_PERMISSIONS.BURN_TOKENS)) {
      return { success: false, error: 'Issuer does not have burning permissions' };
    }

    dispatch({ type: TOKEN_ISSUER_ACTIONS.PROCESS_BURNING, payload: burnData });
    return { success: true };
  };

  const processAssetVerification = async (issuerId) => {
    if (!hasPermission(issuerId, ISSUER_PERMISSIONS.VERIFY_ASSETS)) {
      return { success: false, error: 'Issuer does not have asset verification permissions' };
    }

    return { success: true, verificationId: `verify-${Date.now()}` };
  };

  const processRedemptionRequest = async (issuerId) => {
    if (!hasPermission(issuerId, ISSUER_PERMISSIONS.REDEMPTION_PROCESSING)) {
      return { success: false, error: 'Issuer does not have redemption processing permissions' };
    }

    return { success: true, redemptionId: `redeem-${Date.now()}` };
  };

  const processDeliveryRequest = async (issuerId) => {
    if (!hasPermission(issuerId, ISSUER_PERMISSIONS.PHYSICAL_DELIVERY)) {
      return { success: false, error: 'Issuer does not have delivery processing permissions' };
    }

    return { success: true, deliveryId: `delivery-${Date.now()}` };
  };

  const processAssetNullification = async (issuerId) => {
    if (!hasPermission(issuerId, ISSUER_PERMISSIONS.ASSET_NULLIFICATION)) {
      return { success: false, error: 'Issuer does not have asset nullification permissions' };
    }

    return { success: true, nullificationId: `nullify-${Date.now()}` };
  };

  const getIssuerById = (issuerId) => state.issuers.find((issuer) => issuer.id === issuerId);
  const getActiveIssuers = () => state.issuers.filter((issuer) => issuer.status === ISSUER_STATUS.ACTIVE);
  const getIssuersByCategory = (category) =>
    state.issuers.filter(
      (issuer) => issuer.allowedCategories?.includes(category) && issuer.status === ISSUER_STATUS.ACTIVE
    );
  const getIssuersByRegion = (region) =>
    state.issuers.filter(
      (issuer) => issuer.allowedRegions?.includes(region) && issuer.status === ISSUER_STATUS.ACTIVE
    );

  const normalizeRole = (role) => String(role || '').toLowerCase();

  const isAdmin = state.userRoles.some((role) => normalizeRole(role) === 'admin');
  const isLegalReviewer = state.userRoles.some((role) => normalizeRole(role) === 'legalreviewer');
  const isAuditor = state.userRoles.some((role) => normalizeRole(role) === 'auditor');
  const isAuthorizedTokenizer = state.userRoles.some((role) => normalizeRole(role) === 'authorizedtokenizer');

  const canAccessLegalReviewApi = isAdmin || isLegalReviewer || isAuditor;
  const canAssignOrDecideLegalReview = isAdmin || isLegalReviewer;

  const contextValue = {
    ...state,
    isAdmin,
    isLegalReviewer,
    isAuditor,
    isAuthorizedTokenizer,
    canAccessLegalReviewApi,
    canAssignOrDecideLegalReview,
    addIssuer,
    updateIssuer,
    removeIssuer,
    setCurrentIssuer,
    clearCurrentIssuer,
    loginIssuer,
    refreshProfile,
    loadIssuers,
    fetchTokenizerById,
    hasPermission,
    canOperateInCategory,
    canOperateInLocation,
    grantPermission,
    revokePermission,
    processTokenIssuance,
    processTokenBurning,
    processAssetVerification,
    processRedemptionRequest,
    processDeliveryRequest,
    processAssetNullification,
    getIssuerById,
    getActiveIssuers,
    getIssuersByCategory,
    getIssuersByRegion,
    validateIssuerData
  };

  return <TokenIssuerContext.Provider value={contextValue}>{children}</TokenIssuerContext.Provider>;
};

export const useTokenIssuer = () => {
  const context = useContext(TokenIssuerContext);
  if (!context) {
    throw new Error('useTokenIssuer must be used within a TokenIssuerProvider');
  }
  return context;
};